import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, ListChecks } from 'lucide-react';
import { SchoolApi } from '../../api/client';
import type { ExamBankDetail } from '../../api/types';
import { Badge, Button, EmptyState, ErrorPanel, Modal, PageHeader, PanelCard, PillTabs, Spinner } from '../../components/ui';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { useSchoolGradeSubjects } from '../../../react-query/queries';
import { apiErrorText } from '../../lib/apiError';
import { gradeCompact } from '../../lib/gradeLabel';
import { useMyTeaching, useTaughtSubjects } from '../../lib/teaching';
import { QuestionsPreview } from '../../../pages/Worksheets/components/WorksheetQuestionsPreview';
import { KIND_LABEL, copyDestination, myResultsText } from './bankText';

interface SubjectRow { id: number; name?: string; subject_name?: string }

/** Pick one of the viewer's own grades and a subject they teach there, then copy. */
function CopyDialog({ exam, onClose }: { exam: ExamBankDetail; onClose: () => void }) {
  const navigate = useNavigate();
  const { token, role } = useAuth();
  // A teacher copies only into a grade and subject they teach (homeroom is not enough);
  // admins pick from every grade of the school.
  const teaching = useMyTeaching();
  const taught = useTaughtSubjects();
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades(), enabled: role !== 'teacher' });
  const grades: { id: number; name: string }[] = role === 'teacher'
    ? Array.from(new Map((teaching.data?.subjects ?? []).map((r) => [r.grade.id, r.grade])).values())
    : (gradesQ.data?.grades ?? []);
  const gradesPending = role === 'teacher' ? teaching.isPending : gradesQ.isPending;
  const [pickedGrade, setPickedGrade] = useState<number | null>(null);
  const gradeId = pickedGrade
    ?? (grades.some((g) => g.id === exam.grade?.id) ? exam.grade!.id : grades[0]?.id ?? null);
  const scope = taught.forGrade(gradeId);
  const subjectsQ = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const subjects = ((subjectsQ.data?.subjects ?? []) as SubjectRow[]).filter((s) => scope == null || scope.includes(s.id));
  const [pickedSubject, setPickedSubject] = useState<number | null>(null);
  const subjectId = subjects.some((s) => s.id === pickedSubject) ? pickedSubject
    : subjects.find((s) => s.id === exam.subject?.id)?.id ?? subjects[0]?.id ?? null;

  const copy = useMutation({
    mutationFn: () => SchoolApi.examBankCopy(exam.id, { grade_id: gradeId!, subject_id: subjectId! }),
    onSuccess: (made) => {
      toast.success(`Copied “${made.name}” to your drafts`);
      navigate(copyDestination(made));
    },
  });

  return (
    <Modal isOpen onClose={onClose} size="md" eyebrow="Exam Bank" title="Copy to my class"
      subtitle="You get a draft of your own with the questions, answers and attachments. Edit it, then assign it as usual."
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button icon={<Copy size={16} strokeWidth={1.8} />} loading={copy.isPending}
            disabled={gradeId == null || subjectId == null} onClick={() => copy.mutate()}>
            Copy
          </Button>
        </>
      )}>
      {gradesPending ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : grades.length === 0 ? (
        <p className="text-sm text-muted">You teach no subject yet, so there is nowhere to copy it. Ask your school admin to assign you a subject class.</p>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Grade</p>
            <PillTabs dense scroll label="Grade" value={String(gradeId ?? '')}
              onChange={(k) => { setPickedGrade(Number(k)); setPickedSubject(null); }}
              options={grades.map((g) => ({ key: String(g.id), label: gradeCompact(g.name) }))} />
          </div>
          <div>
            <p className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Subject</p>
            {subjects.length > 0 ? (
              <PillTabs dense scroll label="Subject" value={String(subjectId ?? '')}
                onChange={(k) => setPickedSubject(Number(k))}
                options={subjects.map((s) => ({ key: String(s.id), label: s.subject_name || s.name || 'Subject' }))} />
            ) : (
              <p className="text-sm text-muted">{subjectsQ.isPending ? 'Loading subjects…' : 'You teach no subject in this grade.'}</p>
            )}
          </div>
          {copy.isError && (
            <p role="alert" className="rounded-[10px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">
              {apiErrorText(copy.error, 'Could not copy it. Try again.')}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Exam Bank → one item (/exam-bank/:examId): read only, with its answers (it is released). */
export default function ExamBankDetailPage() {
  const { examId } = useParams();
  const id = Number(examId);
  const q = useQuery({ queryKey: ['examBank.detail', id], queryFn: () => SchoolApi.examBankDetail(id), enabled: id > 0 });
  const [copying, setCopying] = useState(false);
  const trail = [{ label: 'Exam Bank', to: '/exam-bank' }];

  if (q.isPending) return <div className="flex justify-center py-16"><Spinner size="md" /></div>;
  if (q.isError || !q.data) {
    return (
      <>
        <PageHeader title="Exam Bank" trail={trail} />
        <ErrorPanel title="This item is not in the exam bank" body="It may have been unpublished, or its results are not released yet." onRetry={() => q.refetch()} />
      </>
    );
  }
  const e = q.data;
  const facts = [
    e.grade ? gradeCompact(e.grade.name) : null, e.subject?.name,
    `${e.question_count} question${e.question_count === 1 ? '' : 's'}`,
    e.time_minutes ? `${e.time_minutes} min` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        trail={trail}
        title={e.name}
        subtitle={facts}
        actions={<Button icon={<Copy size={18} strokeWidth={1.8} />} onClick={() => setCopying(true)}>Copy to my class</Button>}
      />
      <PanelCard className="mb-[18px]">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <Badge tone={e.kind === 'exam' ? 'navy' : 'info'}>{KIND_LABEL[e.kind]}</Badge>
          <span className="text-muted">By <span className="font-semibold text-ink">{e.creator.name}</span></span>
          <span className="text-muted">Copied {e.copies_count} time{e.copies_count === 1 ? '' : 's'}</span>
          <span className={e.my_results.took > 0 ? 'font-semibold text-ink' : 'text-muted'}>{myResultsText(e.my_results)}</span>
        </div>
        {e.instructions && <p className="mt-3 whitespace-pre-wrap text-sm text-ink">{e.instructions}</p>}
      </PanelCard>
      {e.questions.length === 0 ? (
        <PanelCard><EmptyState compact icon={<ListChecks size={18} strokeWidth={1.8} aria-hidden />} title="No questions" body="This item has no questions." /></PanelCard>
      ) : (
        <QuestionsPreview questions={e.questions} attachments={e.attachments ?? []} />
      )}
      {copying && <CopyDialog exam={e} onClose={() => setCopying(false)} />}
    </div>
  );
}
